// components/HeroSection/HeroSection.js
import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, Container } from "@mui/material";
import FloatingIcons from "./FloatingIcons";
import AnimatedButton from "../../../ui/AnimatedButton";
import GradientText from "../../../ui/GradientText";
import FunModeToggleButton from "../../../../mini-components/FunModeToggleButton";

const HeroSection = ({ isMobile, navigate }) => {
  const titleVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    }
  };

  const letterVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const titleText = "Find Your Perfect Roommate!";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Floating Icons */}
      <FloatingIcons />

      {/* Fun Mode Toggle - Positioned in top right of hero */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, md: 24 },
          right: { xs: 16, md: 24 },
          zIndex: 10,
        }}
      >
        <FunModeToggleButton />
      </Box>
      
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: "center",
            color: "white",
            position: "relative",
            zIndex: 3,
          }}
        >
          {/* Main Title with Character Animation */}
          <motion.div
            variants={titleVariants}
            initial="hidden"
            animate="visible"
          >
            <Typography
              variant={isMobile ? "h3" : "h1"}
              component="div"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: isMobile ? "2.5rem" : "4rem",
                lineHeight: 1.1,
              }}
            >
              {titleText.split("").map((char, index) => (
                <motion.span
                  key={index}
                  variants={letterVariants}
                  style={{ display: "inline-block" }}
                  whileHover={{ 
                    scale: 1.2,
                    color: "#00ff88",
                    transition: { duration: 0.2 }
                  }}
                >
                  {char === " " ? "\u00A0" : char}
                </motion.span>
              ))}
            </Typography>
          </motion.div>

          {/* Subtitle with Gradient */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <GradientText
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: 4,
                fontWeight: 400,
                maxWidth: "600px",
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              Discover roommates who vibe with your lifestyle. 
              From night owls to clean freaks, find your perfect match today!
            </GradientText>
          </motion.div>

          {/* Animated Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <AnimatedButton
              onClick={() => navigate("/fmr/posts")}
              variant="gradient"
              size={isMobile ? "medium" : "large"}
            >
              🚀 Get Started
            </AnimatedButton>
            
            <AnimatedButton
              onClick={() => navigate("/fmr/sign-in")}
              variant="outline"
              size={isMobile ? "medium" : "large"}
            >
              🔑 Login
            </AnimatedButton>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 0.6 }}
            style={{ marginTop: "4rem" }}
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{
                width: "2px",
                height: "40px",
                background: "linear-gradient(180deg, #00ff88, transparent)",
                margin: "0 auto",
                borderRadius: "1px",
              }}
            />
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;